import React from 'react'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'
import uniqueId from 'lodash/uniqueId'

// CONSTANTS
import ROUTES from '../_constants/routes'
import { tenantColors } from '../_constants/colors'

// Components
import { Breadcrumbs } from '../_components/Breadcrumbs'
import Dropzone from '../_components/Dropzone'
import { Button } from '../_components/Button'
import { UploadInvoices } from '../_components/UploadInvoices'
import { OriginateFormHeader } from '../_components/OriginateFormHeader'

// Actions
import { userActions, dealActions } from '../_actions'
import { dealService, documentsService } from '../_services'
import { deleteInvoice } from '../_services/lender.service'

//config
import { APConfig } from '../_configs/accountingPlatform.config'
import { isFeatureEnabled } from '../_helpers/flags'
import { AccountingConnectButton } from './AccountingConnectButton'
import { COMPANY_NAME } from 'xen/constants'

// Styling
const UploadInvoicesPageComponent = styled.div`
  .main-content {
    padding: 50px 0px;
    display: flex;
    flex-direction: column;
    max-width: 800px;
    margin: 0 auto;
    width: 80%;

    & > * {
      margin-bottom: 25px;

      &:last-child {
        margin-bottom: 0px;
      }
    }
  }

  .upload-documents {
    display: flex;
    flex-direction: column;
    margin-top: 15px;
  }

  .skip-step {
    margin-top: 48px;
    text-align: center;
  }
`

class UploadInvoicesPage extends React.Component {
  state = {
    files: [],
    invoices: [],
  }

  handleInvoiceUpload = (acceptedFiles, rejectedFiles) => {
    const newFiles = acceptedFiles.map((file) => {
      file.id = uniqueId()
      return file
    })
    this.setState(
      (state) => ({
        ...state,
        files: [...state.files, ...newFiles],
      }),
      () => {
        this.handleUploadInvoices()
      }
    )
  }

  addInvoices = () => {
    this.state.files.forEach((file) => {
      this.uploadAndCreateInvoice(file)
    })
  }

  removeInvoice = (id, i) => {
    this.setState(
      (state) => ({
        files: state.files.filter((file, index) => i !== index),
        invoices: state.invoices.filter((file, index) => i !== index),
      }),
      () => {
        deleteInvoice(id)
      }
    )
  }

  uploadAndCreateInvoice = (file) => {
    const { business, deal, user } = this.props
    const invoices = this.state.invoices
    const hasInvoice = invoices.find((invoice) => {
      return invoice.file_id === file.id
    })
    if (hasInvoice) {
      return
    }
    dealService.createInvoice(business.id, deal.id).then((resp) => {
      let invoice = resp
      invoice.document_name = file.name
      invoice.file_id = file.id
      let invoicesCopy = [...this.state.invoices, invoice]
      this.setState(
        (state) => ({
          ...state,
          invoices: invoicesCopy,
        }),
        () => {
          documentsService.updateDocument(invoice.document_id, `document_name`, invoice.document_name)
          documentsService.updateDocument(invoice.document_id, `data_source`, invoice.data_source)
          documentsService.updateInvoice(invoice.id, `data_source`, invoice.data_source)

          documentsService.uploadInvoice(file, user.id, false, deal.id, invoice.document_id)
        }
      )
    })
  }

  handleUploadInvoices = () => {
    const { deal, createDeal, business } = this.props
    if (!deal || !deal.deal_status) {
      createDeal(business.id).then((_resp) => {
        this.addInvoices()
      })
    } else {
      this.addInvoices()
    }
  }

  render() {
    const { user, business, tenant, setFlowStep, accountingPlatform, featureFlags } = this.props
    const isConnectedToAccountingPlatform = accountingPlatform && accountingPlatform !== `none`
    const nextStepText = isConnectedToAccountingPlatform ? 'Continue' : 'Skip'
    const noAccounting =
      !APConfig[tenant].quickbooks_enabled && !APConfig[tenant].sage_one_enabled && !APConfig[tenant].xero_enabled
    const accounting_integration = isFeatureEnabled('accounting_integration', featureFlags)

    return (
      <UploadInvoicesPageComponent>
        <Helmet>
          <title>Upload Invoices • {COMPANY_NAME}</title>
        </Helmet>
        <Breadcrumbs activeItem="Your Invoices" />
        <div className="main-content">
          <OriginateFormHeader subtitle={business.business_name || user.email_address} title="Your Invoices" />
          {accounting_integration && !noAccounting ? (
            <AccountingConnectButton
              accountingPlatform={accountingPlatform}
              isConnectedToAccountingPlatform={isConnectedToAccountingPlatform}
            />
          ) : null}

          <div className={`upload-documents`}>
            <Dropzone
              bordercolor={tenantColors[tenant].primary}
              handleUpload={this.handleInvoiceUpload}
              text="Upload invoices, purchase orders, and other receivables"
            />
            {this.state.invoices.length > 0 && (
              <UploadInvoices invoices={this.state.invoices} removeInvoice={this.removeInvoice} />
            )}
          </div>
          <div className={`skip-step`}>
            <Button
              buttonType={`link`}
              onClick={() => setFlowStep(user.id, 'skip_invoices_application')}
              text={nextStepText}
              to={ROUTES.confirm_form}
              type={`button`}
            />
          </div>
        </div>
      </UploadInvoicesPageComponent>
    )
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    setFlowStep: (userId, value) => {
      dispatch(userActions.updateUser('flow_step', value, userId))
    },
    createDeal: (businessId) => {
      dispatch(dealActions.createDeal(businessId))
    },
  }
}

function mapStateToProps(state) {
  const { user, business, env, accounting, deal, featureFlags } = state
  return {
    user,
    business,
    featureFlags,
    tenant: env.tenant,
    accountingPlatform: accounting.platform,
    deal: deal.deal,
  }
}

const connectedUploadInvoicesPage = connect(mapStateToProps, mapDispatchToProps)(UploadInvoicesPage)
export { connectedUploadInvoicesPage as UploadInvoicesPage }
